export type ContactLink = {
  id: string;
  type: "telegram" | "whatsapp" | "phone" | "website" | "custom";
  placement: "storefront" | "seller";
  label: string;
  url: string;
  enabled: boolean;
  position: number;
};

export type StoreSettings = {
  name: string;
  description: string;
  avatarUrl?: string;
  defaultStoreId?: string;
  forceDefaultStore?: boolean;
  contactLinks: ContactLink[];
};

export type StoreOption = {
  id: string;
  name: string;
  description: string;
  avatarUrl?: string;
  productCount: number;
};

export type ProductMedia = {
  id: string;
  url: string;
  position: number;
  altText?: string;
};

export type Product = {
  id: string;
  slug: string;
  sourceChannelId?: string;
  title: string;
  description: string;
  price?: number;
  currency: string;
  availabilityStatus: "available" | "reserved" | "sold" | "hidden";
  visibility: "published" | "hidden";
  expiresAt: string;
  sellerContactUrl?: string;
  sellerContactLabel?: string;
  sellerContactMessage?: string;
  likeCount?: number;
  viewerHasLiked?: boolean;
  media: ProductMedia[];
};

export type ProductPage = {
  items: Product[];
  nextOffset?: number;
};

const demoStore: StoreSettings = {
  name: "Telegram Shop",
  description: "Витрина товаров из Telegram-канала",
  avatarUrl: "/icons/icon.svg",
  contactLinks: [
    {
      id: "demo-contact",
      type: "telegram",
      placement: "storefront",
      label: "Подать объявление",
      url: "https://telegram.me/admin_example",
      enabled: true,
      position: 1
    },
    {
      id: "demo-seller-contact",
      type: "telegram",
      placement: "seller",
      label: "Написать продавцу",
      url: "https://telegram.me/seller_example",
      enabled: true,
      position: 1
    }
  ]
};

const demoProducts: Product[] = [
  {
    id: "demo-1",
    slug: "linen-shirt",
    sourceChannelId: "demo-clothes",
    title: "Льняная рубашка",
    description: "Свободная рубашка из плотного льна. Импортируйте реальные товары через Telegram webhook.",
    price: 49,
    currency: "EUR",
    availabilityStatus: "available",
    visibility: "published",
    expiresAt: new Date(Date.now() + 20 * 86400000).toISOString(),
    sellerContactUrl: "https://telegram.me/roomata",
    sellerContactLabel: "Написать @roomata",
    sellerContactMessage: "Здравствуйте! Интересует товар: Льняная рубашка\nЦена: 49,00 €",
    media: [
      {
        id: "demo-media-1",
        url: "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?auto=format&fit=crop&w=900&q=80",
        position: 1,
        altText: "Льняная рубашка"
      },
      {
        id: "demo-media-1-detail",
        url: "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80",
        position: 2,
        altText: "Льняная рубашка на модели"
      },
      {
        id: "demo-media-1-texture",
        url: "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=900&q=80",
        position: 3,
        altText: "Деталь ткани льняной рубашки"
      }
    ]
  },
  {
    id: "demo-2",
    slug: "ceramic-cup",
    sourceChannelId: "demo-home",
    title: "Керамическая чашка",
    description: "Ручная работа, матовая глазурь, спокойная форма для ежедневного кофе.",
    price: 18,
    currency: "EUR",
    availabilityStatus: "available",
    visibility: "published",
    expiresAt: new Date(Date.now() + 12 * 86400000).toISOString(),
    media: [
      {
        id: "demo-media-2",
        url: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=900&q=80",
        position: 1,
        altText: "Керамическая чашка"
      }
    ]
  }
];

const demoStores: StoreOption[] = [
  { id: "demo-clothes", name: "Одежда", description: "Личные вещи, одежда и аксессуары.", productCount: 1 },
  { id: "demo-home", name: "Дом", description: "Керамика, интерьер и небольшие находки.", productCount: 1 }
];

async function fetchJson<T>(url: string, init?: RequestInit, fallback?: T): Promise<T> {
  const method = init?.method?.toUpperCase() ?? "GET";
  const attempts = method === "GET" ? 4 : 1;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await fetchWithTimeout(url, init);

      if (!response.ok) {
        if (fallback && isLocalDev()) return fallback;
        const details = await response.text().catch(() => "");
        throw new Error(`Request failed: ${response.status}${details ? ` ${details}` : ""}`);
      }

      return response.json() as Promise<T>;
    } catch (error) {
      lastError = error;
      if (fallback && isLocalDev()) return fallback;
      if (attempt === attempts - 1) break;
      await wait(500 * (attempt + 1));
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Request failed");
}

async function fetchWithTimeout(url: string, init?: RequestInit, timeoutMs = 8000) {
  if (!("AbortController" in window)) {
    return Promise.race([
      fetch(url, {
        cache: "no-store",
        headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
        ...init
      }),
      wait(timeoutMs).then(() => {
        throw new Error("Request timed out");
      })
    ]);
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      cache: "no-store",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
      signal: controller.signal
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export const api = {
  async getStore() {
    return fetchJson<StoreSettings>("/api/store", undefined, demoStore);
  },
  async getStores() {
    return fetchJson<StoreOption[]>("/api/stores", undefined, demoStores);
  },
  async getProducts(storeId?: string, viewerDeviceId?: string) {
    const params = new URLSearchParams();
    if (storeId) params.set("store", storeId);
    if (viewerDeviceId) params.set("viewer", viewerDeviceId);
    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";
    const fallback = storeId ? demoProducts.filter((product) => product.sourceChannelId === storeId) : demoProducts;
    return fetchJson<Product[]>(`/api/products${query}`, undefined, fallback);
  },
  async getProductPage(storeId?: string, viewerDeviceId?: string, offset = 0, limit = 30) {
    const params = new URLSearchParams();
    if (storeId) params.set("store", storeId);
    if (viewerDeviceId) params.set("viewer", viewerDeviceId);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    const fallbackItems = storeId ? demoProducts.filter((product) => product.sourceChannelId === storeId) : demoProducts;
    return fetchJson<ProductPage>(`/api/products?${params.toString()}`, undefined, {
      items: fallbackItems.slice(offset, offset + limit),
      nextOffset: offset + limit < fallbackItems.length ? offset + limit : undefined
    });
  },
  async getProduct(slug: string) {
    const fallback = demoProducts.find((product) => product.slug === slug) ?? demoProducts[0];
    return fetchJson<Product>(`/api/products/${slug}`, undefined, fallback);
  },
  async updateProduct(id: string, body: Omit<Partial<Product>, "price"> & { price?: number | null }) {
    return fetchJson<Product>(`/api/admin/products/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  },
  async getAdminProducts(options: { query?: string; storeId?: string; offset?: number; limit?: number } = {}) {
    const params = new URLSearchParams();
    if (options.query) params.set("q", options.query);
    if (options.storeId) params.set("store", options.storeId);
    params.set("offset", String(options.offset ?? 0));
    params.set("limit", String(options.limit ?? 40));
    return fetchJson<ProductPage>(`/api/admin/products?${params.toString()}`);
  },
  async deleteProduct(id: string) {
    return fetchJson<{ ok: true }>(`/api/admin/products/${id}`, { method: "DELETE" });
  },
  async adminLogin(password: string) {
    return fetchJson<{ ok: true }>("/api/admin/login", { method: "POST", body: JSON.stringify({ password }) });
  },
  async adminLogout() {
    return fetchJson<{ ok: true }>("/api/admin/logout", { method: "POST", body: "{}" });
  },
  async updateContacts(contactLinks: ContactLink[]) {
    return fetchJson<StoreSettings>("/api/admin/store/contacts", {
      method: "PATCH",
      body: JSON.stringify({ contactLinks })
    });
  },
  async likeProduct(productId: string, deviceId: string) {
    return fetchJson<{ likeCount: number; viewerHasLiked: boolean }>(`/api/products/${productId}/like`, {
      method: "POST",
      body: JSON.stringify({ deviceId })
    });
  },
  async getAdminMe() {
    return fetchJson<{ ok: true }>("/api/admin/me");
  }
};

function isLocalDev() {
  return ["localhost", "127.0.0.1", ""].includes(window.location.hostname);
}
